import React, { useEffect, useState, useRef } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { generateAxiosInstanceInstitute } from '../hooks/useAuthorizedRequestForInstitute';
import { generateAxiosInstanceCommonFileUpload } from '../hooks/useAuthorizedRequestForFileUpload';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../components/ModalsCommonFunctions'; import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import Pagination from '../components/Pagination';
import Select from 'react-select';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CModal } from '@coreui/react';
import { aboutUsDetailsFormValidation, subscriptionFormValidation } from '../validation/Validation';
import { ZodError } from 'zod';
import { ErrorMessage } from '../validation/validationError';

const AboutUsAppMenu = () => {
    const authorizedAxios = generateAxiosInstance();
    const authorizedAxiosCommonFileUpload = generateAxiosInstanceCommonFileUpload();

    const [aboutUsDetails, setAboutUsDetails] = useState("");
    const [aboutUsDetailsList, setAboutUsDetailsList] = useState([]);

    const [visible, setVisible] = useState(false);
    const [details, setDetails] = useState("");

    const [editId, setEditId] = useState(null);
    const [errors, setErrors] = useState([]);

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["subscriptionName", "amount"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getAbountUsDetailsList();
    }, []);

    const getAbountUsDetailsList = async () => {
        const apiUrl = `/about-us`;
        try {
            const response = await authorizedAxios.get(apiUrl);
            setAboutUsDetailsList(response.data.result);
        } catch (error) {
            setAboutUsDetailsList([]);
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            console.error('Error fetching data:', errorMsg);
        }
    }


    const handleDetailChange = (editor) => {
        let value = editor.getData();
        setAboutUsDetails(value);

        if (value.trim().length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== 'aboutUsDetails'));
        }
    }

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getAbountUsDetailsList();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setAboutUsDetailsList(await searchArray("A", `/about-us`, searchKeys, searchValue.trim()))
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(aboutUsDetailsList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(aboutUsDetailsList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > aboutUsDetailsList.length) {
            endIndex = aboutUsDetailsList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(aboutUsDetailsList.slice(startIndex, endIndex));
    }, [aboutUsDetailsList, currPage, itemsPerPage]);


    const addAboutUsDetails = async () => {
        try {
            await aboutUsDetailsFormValidation.parseAsync({ aboutUsDetails });
            setErrors([]);
            setLoading(true);
            const apiUrl = `/about-us`;
            const response = await authorizedAxios.post(apiUrl, { aboutUsDetails });

            await getAbountUsDetailsList();
            setSaveId(response.data.result.id);
            setAboutUsDetails("");
            setEditId(null);

            Swal.fire(
                { text: response.data.message }
            );
        } catch (err) {
            if (err instanceof ZodError) {
                setErrors(err.errors);
            } else {
                const errorMsg = err?.response?.data?.message || 'Something went wrong!';
                Swal.fire({ text: errorMsg });
            }
        } finally {
            setLoading(false);
        }
    }

    function uploadAdapter(loader) {
        return {
            upload: () => {
                return new Promise((resolve, reject) => {
                    const body = new FormData();
                    loader.file.then(async (file) => {
                        body.append("file", file);
                        const apiUrl = "uploadimage";
                        authorizedAxiosCommonFileUpload && authorizedAxiosCommonFileUpload.post(apiUrl, body)
                            .then((res) => {
                                resolve({ default: res.data.result });
                            })
                            .catch((err) => {
                                reject(err.response.data.message);
                            });
                    });
                });
            },
        };
    }

    function uploadPlugin(editor) {
        editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
            return uploadAdapter(loader);
        };
    }


    const updateAboutUs = async () => {
        try {
            setLoading(true);
            const apiUrl = `/about-us?id=${editId}`;
            const response = await authorizedAxios.put(apiUrl, { aboutUsDetails });
            await getAbountUsDetailsList();
            setEditId(null);
            setAboutUsDetails("");
            Swal.fire(
                { text: response.data.message }
            );
        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            Swal.fire(
                { text: errorMsg }
            );
        } finally {
            setLoading(false);
        }
    }

    const deleteAboutUs = async (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            try {
                setLoading(true);
                const apiUrl = `/about-us?id=${id}`;
                const response = await authorizedAxios.delete(apiUrl);
                const page = deleteItem(currPage, itemsPerPage, aboutUsDetailsList.length);
                await getAbountUsDetailsList();
                setCurrPage(page);

                Swal.fire(
                    { text: response.data.message }
                );
            } catch (error) {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        }
    }

    const changeActive = async (id) => {
        try {
            setLoading(true);
            const apiUrl = `/active-inactive-about-us?id=${id}`;
            const response = await authorizedAxios.patch(apiUrl);
            await getAbountUsDetailsList();
            Swal.fire({ text: response.data.message });
        } catch (error) {
            const errorMsg = error?.response?.data.message || 'Something went wrong!';
            Swal.fire({ text: errorMsg });
        } finally {
            setLoading(false);
        }
    }

    const handleInputReset = () => {
        setAboutUsDetails("");
        setDetails("");
        setEditId(null);
        setErrors([]);
    }



    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb my-0 ms-2">
                        <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                            <span>Dashboard</span>
                        </li>
                        <li className="breadcrumb-item active"><span>About Us</span></li>
                    </ol>
                </nav>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa-solid fa-circle-info"></i>About Us</b></h4>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">

                                                <div className="col-md-12">
                                                    <label for="inputPassword" name="details" className="col-form-label"><span style={{ color: "red" }}>*</span>About Us Details :</label>
                                                    <CKEditor
                                                        editor={ClassicEditor}
                                                        data={aboutUsDetails}
                                                        onReady={editor => {
                                                            // You can store the "editor" and use when it is needed.
                                                            // console.log('Editor is ready to use!', editor);
                                                        }}
                                                        onChange={(event, editor) => {
                                                            // console.log(event)
                                                            handleDetailChange(editor)
                                                        }}

                                                        config={{
                                                            extraPlugins: [uploadPlugin],
                                                        }}

                                                        onInit={(editor) => {
                                                            // Register the custom upload adapter
                                                            uploadPlugin(editor);
                                                        }}
                                                    />
                                                    <ErrorMessage errors={errors} field="aboutUsDetails" />
                                                </div>

                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                    <div className="col-auto new_button">
                                                        {!editId && <button className="btn btn-primary" style={{ marginRight: '20px' }} type="button" onClick={addAboutUsDetails}>Save</button>}
                                                        <button className="btn btn-danger delete_icon delete_icon " type="button" style={{ marginRight: "20px" }} onClick={handleInputReset}>Reset</button>
                                                        {
                                                            editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateAboutUs} disabled={!editId}>Update</button>)
                                                        }
                                                    </div>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col">Sr. No.</th>
                                                                        <th scope="col">Details</th>
                                                                        <th scope="col">Active/Inactive</th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((data, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row">{startIndex + index + 1}</th>
                                                                                        <td>
                                                                                            <button className="btn btn-primary view_icon" type="button" onClick={() => {
                                                                                                setDetails(data?.details);
                                                                                                setVisible(1)
                                                                                            }}><i class="fa fa-eye"></i></button>
                                                                                        </td>
                                                                                        <td className='toggle_switch_id'>
                                                                                            <div className={`toggle-container margin-inline ${data.isActive ? 'active' : ''}`} onClick={() => changeActive(data.id)}>
                                                                                                <div className="toggle-background"></div>
                                                                                                <div className="toggle-switch"></div>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" /*disabled={editId == sub.id}*/ onClick={() => {
                                                                                                setEditId(data.id);
                                                                                                setAboutUsDetails(data.details)
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>
                                                                                            <button className="btn btn-outline-danger delete_icon" type="button" disabled={editId === data.id} onClick={() => deleteAboutUs(data.id)}> <i class="fa-light fa-trash-can"></i></button>
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No About Us Details Available</th>
                                                                            </tr>)
                                                                    }
                                                                </tbody>
                                                            </table>
                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={aboutUsDetailsList.length} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            <CModal
                backdrop="static"
                visible={visible}
                onClose={() => setVisible(false)}
                aria-labelledby="LiveDemoExampleLabel"
            >
                <div className="book book-modal">
                    <div className="table-header-like">Subscription Details</div>
                    <div
                        className="page"
                        dangerouslySetInnerHTML={{
                            __html: `<style>.ckContent img{max-width:100% !important; height: auto !important}</style><div class="ck-content">${details}</div>`,
                        }}
                    ></div>
                    <button
                        className="btn btn-outline-warning edit_icon"
                        type="button"
                        onClick={() => setVisible(false)}
                    >
                        Close
                    </button>
                </div>
            </CModal>

        </>
    )
};

export default AboutUsAppMenu;